import { Info } from "lucide-react";
import { useState } from "react";

import { useAdminPromotionRequests } from "@/lib/api";
import { useUser } from "@/lib/user-context";

import { DecideDialog } from "./decide-dialog";
import { RequestTable } from "./request-table";
import type { PendingDecision } from "./types";

/**
 * The promotion queue: owners ask for test/prod (from the UI or from Claude
 * through MCP), admins decide here. Approving runs the same promotion as a
 * project's Promote button; nothing is promoted by the request itself.
 */
export function AdminPromotionRequestsPage() {
  const { user } = useUser();
  const { data: requests = [], isLoading, isError } = useAdminPromotionRequests();
  const [decision, setDecision] = useState<PendingDecision | null>(null);

  const openCount = requests.filter((r) => r.status === "pending" || r.status === "failed").length;

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      <div>
        <h2 className="text-primary text-2xl font-normal">Promotion requests</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Requests to promote a project to test or prod &mdash; {openCount} waiting
        </p>
      </div>

      <div className="border-border bg-muted/40 mt-6 flex items-start gap-2 rounded-lg border p-3">
        <Info className="text-muted-foreground mt-0.5 size-4 shrink-0" />
        <p className="text-muted-foreground text-xs">
          Approving runs the promotion straight away, exactly as the Promote button on the project
          does: it creates the branch, opens the pull request and sets up the environment. If it
          fails, the request is marked failed and you can approve it again to retry, or reject it.
        </p>
      </div>

      <div className="bg-card border-border mt-6 overflow-hidden rounded-lg border">
        {isLoading ? (
          <div className="text-muted-foreground py-16 text-center text-sm">
            Loading promotion requests...
          </div>
        ) : isError ? (
          <div className="text-destructive py-16 text-center text-sm">
            Could not load the promotion requests.
          </div>
        ) : (
          <RequestTable
            requests={requests}
            currentUserEmail={user?.email ?? null}
            onDecide={setDecision}
          />
        )}
      </div>

      <DecideDialog
        decision={decision}
        onOpenChange={(open) => {
          if (!open) {
            setDecision(null);
          }
        }}
      />
    </div>
  );
}
